import React from "react";
import { Column, Columns, Stack } from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import { AccountPayer, FormValues } from "./types";
import FormFieldLayout from "./FormComponents/FormFieldLayout";
import ReadOnlyTextField from "./FormComponents/ReadOnlyTextField";
import {
  capitalizeFirstChar,
  getSiteConditionsLabels,
  hasTotalLoadDetails,
  meterLocationOptions,
  populateTotalLoadSummary,
  pressureOptions,
} from "./helper";
import ReadOnlyFieldWrapper from "./ReadOnlyFieldWrapper";
import { usePlumberName } from "src/components/Hooks/usePlumberName";
import { useSiteSupervisorName } from "../Hooks/useSiteSupervisorName";
import RequestorInfo from "./RequestorInfo";
import ReadOnlyUploadedFile from "./FormComponents/ReadOnlyUploadedFile";
import ReadOnlyContact from "./FormComponents/ReadOnlyContact";
import { State } from "../Premises";

const ReadOnlyGasBothBlock = () => {
  const { values } = useFormikContext<FormValues>();

  const {
    firstName: licensedPlumberFirstName,
    lastName: licensedPlumberLastName,
    email: licensedPlumberEmail,
    phone: licensedPlumberPhone,
    licenseNumber: licensedPlumberLicenseNumber,
  } = usePlumberName(
    values.both?.licensedPlumber,
    values.requestingCompany?.msaNumber
  );

  const {
    firstName: siteSupervisorFirstName,
    lastName: siteSupervisorLastName,
    email: siteSupervisorEmail,
    phone: siteSupervisorPhone,
  } = useSiteSupervisorName(
    values.siteSupervisor,
    values.requestingCompany?.msaNumber
  );

  return (
    <Stack
      space={{
        xs: "xxlarge",
        md: "xxxlarge",
      }}
    >
      {/* Form container */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xlarge",
        }}
      >
        {/* Kraken related fields */}
        <ReadOnlyFieldWrapper
          value={values.both.accountPayer === AccountPayer.COMPANY}
        >
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Account number"
              value={values.requestingCompany?.msaNumber || "Pending"}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <FormFieldLayout>
          <ReadOnlyTextField
            label="Meter Installation Registration Number"
            value={values.both?.allocateNmiOrderNumber || "Pending"}
          />
        </FormFieldLayout>

        <FormFieldLayout>
          <ReadOnlyTextField
            label="Supply Service Order Number"
            value={values.both?.supplyServiceOrderNumber || "Pending"}
          />
        </FormFieldLayout>

        <FormFieldLayout>
          <ReadOnlyTextField
            label="Meter Service Order Number"
            value={values.both?.meterServiceOrderNumber || "Pending"}
          />
        </FormFieldLayout>

        {/* End Kraken related fields */}

        <ReadOnlyFieldWrapper value={values.both?.buildingType}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="New or existing building"
              value={capitalizeFirstChar(values.both?.buildingType)}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.premiseAddress?.streetNumber}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Street No."
              value={values.premiseAddress?.streetNumber}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.both?.pressure}>
          <FormFieldLayout>
            <Columns collapseBelow="sm" space={{ xs: "xlarge", md: "medium" }}>
              <Column>
                <ReadOnlyTextField
                  label="Pressure"
                  value={
                    pressureOptions.find(
                      (option) => option.value === values.both?.pressure
                    )?.label || values.both?.pressure
                  }
                />
              </Column>
              {values.both?.pressure === "0" ? (
                <Column>
                  <ReadOnlyTextField
                    label="Other pressure value (kPa)"
                    value={values.both?.otherPressure}
                  />
                </Column>
              ) : null}
            </Columns>
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper
          value={
            values.both.appliancesChecklistFiles &&
            values.both.appliancesChecklistFiles.length > 0
          }
        >
          {values.both.appliancesChecklistFiles?.map((attachment) => (
            <ReadOnlyUploadedFile
              key={attachment.attachmentId}
              attachment={attachment}
            />
          ))}
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.both?.totalLoad}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Total load, MJ/h"
              value={values.both?.totalLoad ?? ""}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper
          value={hasTotalLoadDetails(values.both?.totalLoadDetails)}
        >
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Appliances"
              value={populateTotalLoadSummary(values.both?.totalLoadDetails)}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.both?.siteConditions?.length > 0}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Site conditions"
              value={getSiteConditionsLabels(values.both?.siteConditions)}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.both?.otherSiteCondition}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Other site condition"
              value={values.both?.otherSiteCondition}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.both?.meterLocation}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Meter location"
              value={
                meterLocationOptions.find(
                  (option) => option.value === values.both?.meterLocation
                )?.label || values.both?.meterLocation
              }
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>

        <ReadOnlyFieldWrapper value={values.both?.otherMeterLocation}>
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Other meter location"
              value={values.both?.otherMeterLocation}
            />
          </FormFieldLayout>
        </ReadOnlyFieldWrapper>
        {/* Contacts */}
        <ReadOnlyFieldWrapper
          value={
            values.both?.licensedPlumber &&
            !values.both?.homeUserLicencedPlumber
          }
        >
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
            data-id="readOnlyGasBothBlock.licensedPlumber.column"
          >
            {/* Fields */}
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Licenced plumber"
                firstName={licensedPlumberFirstName}
                lastName={licensedPlumberLastName}
                email={licensedPlumberEmail}
                phone={licensedPlumberPhone}
                licenceNumber={licensedPlumberLicenseNumber}
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>
        <ReadOnlyFieldWrapper value={values.both?.homeUserLicencedPlumber}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
            data-id="readOnlyGasBothBlock.homeUserLicencedPlumber.column"
          >
            {/* Fields */}
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Licenced plumber"
                firstName={values.both?.homeUserLicencedPlumber?.firstName}
                lastName={values.both?.homeUserLicencedPlumber?.lastName}
                email={values.both?.homeUserLicencedPlumber?.email}
                phone={values.both?.homeUserLicencedPlumber?.phoneNumber}
                licenceNumber={
                  values.both?.homeUserLicencedPlumber?.licenseNumber
                }
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>

        {values.state === State.VIC && (
          <FormFieldLayout>
            <ReadOnlyTextField
              label="Life support required"
              value={
                values.both?.lifeSupport === undefined
                  ? "N/A"
                  : values.both?.lifeSupport
                  ? "Yes"
                  : "No"
              }
            />
          </FormFieldLayout>
        )}

        <ReadOnlyFieldWrapper value={values.siteSupervisor}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
            data-id="readOnlyGasBothBlock.siteSupervisor.column"
          >
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Site supervisor"
                firstName={siteSupervisorFirstName}
                lastName={siteSupervisorLastName}
                email={siteSupervisorEmail}
                phone={siteSupervisorPhone}
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>
        <ReadOnlyFieldWrapper value={values.both.homeUserSiteSupervisor}>
          <Columns
            collapseBelow="md"
            space={{
              xs: "none",
              md: "xlarge",
            }}
            alignY="stretch"
            data-id="readOnlyGasBothBlock.homeUserSiteSupervisor.column"
          >
            <Column width="2/3">
              <ReadOnlyContact
                contactType="Site supervisor"
                firstName={values.both.homeUserSiteSupervisor?.firstName}
                lastName={values.both.homeUserSiteSupervisor?.lastName}
                email={values.both.homeUserSiteSupervisor?.email}
                phone={values.both.homeUserSiteSupervisor?.phoneNumber}
              />
            </Column>
          </Columns>
        </ReadOnlyFieldWrapper>
        {/* End Contacts */}
        <ReadOnlyFieldWrapper
          value={
            values.both.sitePlanFiles && values.both.sitePlanFiles.length > 0
          }
        >
          {values.both.sitePlanFiles?.map((attachment) => (
            <ReadOnlyUploadedFile
              key={attachment.attachmentId}
              attachment={attachment}
            />
          ))}
        </ReadOnlyFieldWrapper>
        <ReadOnlyFieldWrapper
          value={
            values.both.shipperDocumentsFiles &&
            values.both.shipperDocumentsFiles.length > 0
          }
        >
          {values.both.shipperDocumentsFiles?.map((attachment) => (
            <ReadOnlyUploadedFile
              key={attachment.attachmentId}
              attachment={attachment}
            />
          ))}
        </ReadOnlyFieldWrapper>
        <FormFieldLayout>
          <RequestorInfo
            requesterName={values.both?.requesterName || ""}
            submittedDateTime={values.both?.submittedDateTime || ""}
          />
        </FormFieldLayout>
      </Stack>
      {/* End Form container */}
    </Stack>
  );
};

export default ReadOnlyGasBothBlock;
